/**
 * Type definitions for Inquiry management in marketplace
 * Handles customer inquiries for products, status tracking, and analytics
 */

/**
 * Inquiry status lifecycle:
 * NEW -> CONTACTED -> CONVERTED -> CLOSED
 * or
 * NEW -> CLOSED
 */
export enum InquiryStatus {
  NEW = 'NEW',
  CONTACTED = 'CONTACTED',
  CONVERTED = 'CONVERTED',
  CLOSED = 'CLOSED',
}

/**
 * Complete inquiry data from API response
 */
export interface Inquiry {
  id: string;
  productId: string;
  productName: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  message: string;
  // B2B Inquiry Fields
  quantity: number | null;
  quantityUnit: string | null;
  location: string | null;
  status: InquiryStatus;
  estimatedQuote: number | null;
  createdAt: string; // ISO datetime
  contactedAt: string | null;
  convertedAt: string | null;
  closedAt: string | null;
}

/**
 * Inquiry row data for table display
 * Includes formatted/derived fields
 */
export interface InquiryRow {
  id: string;
  date: string; // Formatted date
  product: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  message: string;
  // B2B Inquiry Fields
  quantity: number | null;
  quantityUnit: string | null;
  location: string | null;
  status: InquiryStatus;
  estimatedQuote: number | null;
  createdAt: string;
  contactedAt: string | null;
  convertedAt: string | null;
  closedAt: string | null;
}

/**
 * Filter options for inquiry queries
 */
export interface InquiryFilters {
  status: 'ALL' | InquiryStatus;
  productId?: string;
  search?: string; // Search in customer name or email
  sortBy?: 'createdAt' | 'status' | 'customerName';
  sortOrder?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/**
 * Paginated response from API
 */
export interface PaginatedInquiriesResponse {
  content: Inquiry[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

/**
 * Request payload for status update
 */
export interface UpdateInquiryStatusRequest {
  status: InquiryStatus;
  notes?: string;
}

/**
 * Custom error class for inquiry API operations
 */
export class InquiriesApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = 'InquiriesApiError';
  }
}

/**
 * Hook return types
 */
export interface UseInquiriesReturn {
  inquiries: InquiryRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
}

export interface UseUpdateInquiryStatusReturn {
  updateStatus: (inquiryId: string, status: InquiryStatus, notes?: string) => Promise<Inquiry>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Product data for filter dropdown
 */
export interface ProductForFilter {
  id: string;
  name: string;
}

/**
 * Inquiry statistics
 */
export interface InquiryStats {
  totalInquiries: number;
  newInquiries: number;
  contactedInquiries: number;
  convertedInquiries: number;
  closedInquiries: number;
  conversionRate: number;
}
